import React, { useState, useEffect } from 'react';
import './App.css';

interface FormData {
    patientId: string;
    email: string;
    sessionNumber: string;
    date: string;
    startTime: string;
    endTime: string;
    firstName: string;
    lastName: string;
    sex: string;
    dateOfBirth: string;
    study: string;
    clinicalGroup: string;
    reportNeeded: string;
}

const App: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        patientId: '',
        email: '',
        sessionNumber: '',
        date: '',
        startTime: '',
        endTime: '',
        firstName: '',
        lastName: '',
        sex: '',
        dateOfBirth: '',
        study: '',
        clinicalGroup: 'Treatment',
        reportNeeded: '',
    });

    const [errors, setErrors] = useState<{ [key in keyof FormData]?: string }>({});

    useEffect(() => {
        const password = "intruthpractitionerpassword2024";
        const userPassword = prompt("Please enter the password to access this page:");
        if (userPassword === password) {
            setIsAuthenticated(true);
        } else {
            alert("Access Denied");
        }
    }, []);

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Keep ISO date (YYYY-MM-DD) in state; do not reformat for inputs or submission
    setFormData({ ...formData, [name]: value });

        if (name === 'email' && value && !validateEmail(value)) {
            setErrors((prevErrors) => ({ ...prevErrors, email: 'Invalid email format' }));
        } else {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: { [key in keyof FormData]?: string } = {};
        Object.keys(formData).forEach((key) => {
            if (!formData[key as keyof FormData]) {
                newErrors[key as keyof FormData] = 'This field is required';
            }
        });

        if (formData.email && !validateEmail(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            const apiEndpoint = "https://aee2judiak.execute-api.ap-southeast-2.amazonaws.com/prod/study-group-user-list";

            const response = await fetch(apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
                mode: "cors",
            });

            if (response.ok) {
                const result = await response.json();
                console.log("Form submitted successfully:", result);
                alert("Form submitted successfully");
            } else {
                console.error("Failed to submit form:", response.statusText);
                alert("Failed to submit form. Please try again.");
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            alert("An error occurred. Please try again.");
        }
    };

    if (!isAuthenticated) {
        return <div><h1>Access Denied</h1></div>;
    }

    return (
        <div className="form-container">
            <div className="form-header">Patient Acquisition Log</div>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Patient ID:<span className="required">*</span></label>
                    <input
                        type="text"
                        name="patientId"
                        value={formData.patientId}
                        onChange={handleInputChange}
                        className={errors.patientId ? 'input-error' : ''}
                    />
                    {errors.patientId && <span className="error">{errors.patientId}</span>}
                </div>

                <div className="form-group">
                    <label>Email:<span className="required">*</span></label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={errors.email ? 'input-error' : ''}
                    />
                    {errors.email && <span className="error">{errors.email}</span>}
                </div>

                <div className="form-group">
                    <label>Session Number:<span className="required">*</span></label>
                    <input
                        type="text"
                        name="sessionNumber"
                        value={formData.sessionNumber}
                        onChange={handleInputChange}
                        className={errors.sessionNumber ? 'input-error' : ''}
                    />
                    {errors.sessionNumber && <span className="error">{errors.sessionNumber}</span>}
                </div>

                <div className="form-group">
                    <label>Session Date:<span className="required">*</span></label>
                    <input
                        type="date"
                        name="date"
                        value={formData.date || ''}
                        onChange={handleInputChange}
                        className={errors.date ? 'input-error' : ''}
                    />
                    {errors.date && <span className="error">{errors.date}</span>}
                </div>

                <div className="form-group">
                    <label>Start Time:<span className="required">*</span></label>
                    <input
                        type="time"
                        name="startTime"
                        value={formData.startTime || ''}
                        onChange={handleInputChange}
                        className={errors.startTime ? 'input-error' : ''}
                    />
                    {errors.startTime && <span className="error">{errors.startTime}</span>}
                </div>

                <div className="form-group">
                    <label>End Time:<span className="required">*</span></label>
                    <input
                        type="time"
                        name="endTime"
                        value={formData.endTime || ''}
                        onChange={handleInputChange}
                        className={errors.endTime ? 'input-error' : ''}
                    />
                    {errors.endTime && <span className="error">{errors.endTime}</span>}
                </div>

                <div className="form-group">
                    <label>First Name:<span className="required">*</span></label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={errors.firstName ? 'input-error' : ''}
                    />
                    {errors.firstName && <span className="error">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                    <label>Last Name:<span className="required">*</span></label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={errors.lastName ? 'input-error' : ''}
                    />
                    {errors.lastName && <span className="error">{errors.lastName}</span>}
                </div>

                <div className="form-group">
                    <label>Sex:<span className="required">*</span></label>
                    <div className="radio-group">
                        <label>
                            <input
                                type="radio"
                                name="sex"
                                value="Male"
                                checked={formData.sex === 'Male'}
                                onChange={handleInputChange}
                            /> Male
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="sex"
                                value="Female"
                                checked={formData.sex === 'Female'}
                                onChange={handleInputChange}
                            /> Female
                        </label>
                    </div>
                    {errors.sex && <span className="error">{errors.sex}</span>}
                </div>

                <div className="form-group">
                    <label>Date of Birth (DD-MM-YYYY):<span className="required">*</span></label>
                    <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth || ''}
                        onChange={handleInputChange}
                        className={errors.dateOfBirth ? 'input-error' : ''}
                    />
                    {errors.dateOfBirth && <span className="error">{errors.dateOfBirth}</span>}
                </div>

                <div className="form-group">
                    <label>Study:<span className="required">*</span></label>
                    <select
                        name="study"
                        value={formData.study}
                        onChange={handleInputChange}
                        className={errors.study ? 'input-error' : ''}
                    >
                        <option value="">Choose...</option>
                        <option value="RTT-Pilot">RTT-Pilot</option>
                    </select>
                    {errors.study && <span className="error">{errors.study}</span>}
                </div>

                <div className="form-group">
                    <label>Clinical Group:<span className="required">*</span></label>
                    <select
                        name="clinicalGroup"
                        value={formData.clinicalGroup}
                        onChange={handleInputChange}
                        className={errors.clinicalGroup ? 'input-error' : ''}
                    >
                        <option value="Treatment">Treatment</option>
                        <option value="Control">Control</option>
                    </select>
                    {errors.clinicalGroup && <span className="error">{errors.clinicalGroup}</span>}
                </div>

                <div className="form-group">
                    <label>Any Discrepancies to usual treatment protocol?:<span className="required">*</span></label>
                    <input
                        type="text"
                        name="reportNeeded"
                        placeholder={"e.g. 10 minute meditation added on top of usual protocol of hypnotherapy+CBT+NLP"}
                        value={formData.reportNeeded}
                        onChange={handleInputChange}
                        className={errors.reportNeeded ? 'input-error' : ''}
                    />
                    {errors.reportNeeded && <span className="error">{errors.reportNeeded}</span>}
                </div>

                <div className="button-container">
                    <button type="submit" className="submit-button">Submit</button>
                </div>
            </form>
        </div>
    );
};

export default App;
